import React, { useEffect, useState } from 'react'
import { zodResolver } from "@hookform/resolvers/zod";
import InputBox from '../../../Components/Input'
import { FiMail, FiPhone } from 'react-icons/fi'
import useMyAgencyData from '../../../hooks/useMyAgencyData';
import { useFeature } from '../../../hooks/useFeature';
import { notificationsFormSchema, settingsFormSchema } from './Schema/SettingsSchema';
import { SubmitHandler, useForm } from 'react-hook-form';
import { UpdateAgency } from '../../../type/Agency';
import { API_ENDPOINTS } from '../../../utils/api';
import createAxiosInstance from "../../../utils/axiosInstance";
import { useLocation, useNavigate } from 'react-router-dom';
import ModalDailog from '../../../Components/Modal';
import Loader from '../../../Components/Loader';
import { showToast } from '../../../Components/ToastNotification';

const SettingsPage = () => {
  const navigate = useNavigate();
  const axiosInstance = createAxiosInstance();
  const agency = useMyAgencyData();
  const canAnswer = useFeature("bedfinder:canAnswerRequest");
  const isSender = useFeature("bedfinder:canSendRequest");
  const isReceiver = useFeature("bedfinder:canReceiveRequest");
  const [isSettingConfirmModelOpen, setIsSettingConfirmModelOpen] = useState(false);
  const [isLoding, setIsLoding] = useState(true);
  const location = useLocation();

  const closeIsSettingConfirmModel = () => {
    setIsSettingConfirmModelOpen(false);
  }

  const openSettingConfirmModel = () => {
    setIsSettingConfirmModelOpen(true);
  }

  const isObserver = !canAnswer && !isSender && isReceiver;
  const isReferral = !canAnswer && isSender && !isReceiver;
  const needContacts = canAnswer || !(isObserver || isReferral);

  const schema = !needContacts ? notificationsFormSchema : settingsFormSchema;

  useEffect(() => {
    if (agency) {
      reset({
        details: {
          notifications: [
            {
              email: agency?.details?.notifications?.[0]?.email || "",
              sms: agency?.details?.notifications?.[0]?.sms || "",
              voice: agency?.details?.notifications?.[0]?.voice || "",
            },
          ],
          referral: {
            phone: agency?.details?.referral?.phone || "",
            email: agency?.details?.referral?.email || "",
          },
        },
      });
      setIsLoding(false);
    }
  }, [agency]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateAgency>({
    defaultValues: {
      details: {
        notifications: [
          {
            email: agency?.details?.notifications?.[0]?.email || "",
            sms: agency?.details?.notifications?.[0]?.sms || "",
            voice: agency?.details?.notifications?.[0]?.voice || "",
          },
        ],
        referral: {
          phone: agency?.details?.referral?.phone || "",
          email: agency?.details?.referral?.email || "",
        },
      },
    },
    resolver: zodResolver(schema),
  });

  const submit: SubmitHandler<UpdateAgency> = async data => {
    try {
      if (agency) {
        setIsLoding(true);
        const payload = {
          "details.referral": data.details?.referral,
          "details.notifications": data.details?.notifications,
          "details.phone": data.details?.notifications?.[0]?.phone,
          "details.sms": data.details?.notifications?.[0]?.sms,
          "details.email": data.details?.notifications?.[0]?.email,
          "details.voice": data.details?.notifications?.[0]?.voice,
          wizardStatus: data.wizardStatus,
        };

        const saveSettings = await axiosInstance.put(API_ENDPOINTS.BEDFINDER.UPDATEUSERSETTINGS.replace("{agencyId}", agency.agencyId), payload);

        if (saveSettings.status === 200) {
          closeIsSettingConfirmModel();
          reset(data);
          setIsLoding(false);
          showToast("Settings were saved.", "success");
        }
      }
    } catch (error) {
      setIsLoding(false);
      console.log(error);
    }
  };

  return (
    <>
      {isLoding && <Loader />}
      <div className='bg-white'>
        <form id="settings" onSubmit={handleSubmit(openSettingConfirmModel)}>
          <div className="container py-4 md:py-10 2xl:py-[70px]">
            <div className="alert p-3 rounded-lg text-warning bg-warning bg-opacity-10 mb-4">
              Notifications preferences are shared between all users that have access to the agency's account.
            </div>
            <div className="flex flex-wrap -mx-4 mt-10">
              <div className="md:w-1/3 px-4">
                <h2 className='text-base lg:text-lg 2xl:text-2xl font-semibold'>Notification Preferences</h2>
                {(isObserver || needContacts) && (
                  <p className='text-gray-600 text-sm 2xl:text-base'>
                    When another agency needs a bed, I want to receive the following
                    notifications:
                  </p>
                )}
                {isReferral ?? (
                  <p className='text-gray-600 text-sm 2xl:text-base'>
                    When my agency creates a bed request, I want to receive a reminder
                    notification:
                  </p>
                )}
              </div>


              <div className="w-full md:w-2/3 px-4 mt-3 md:mt-0">
                <div className="card p-6">
                  {(isObserver || needContacts) && (
                    <div className="mb-5">
                      <InputBox
                        type="text"
                        placeholder="Enter a mobile number"
                        label="Send us a text message"
                        iconRight={<FiPhone />}
                        {...register("details.notifications.0.sms")}
                      />
                      <p className='text-error'>{errors.details?.notifications?.[0]?.sms?.message}</p>
                    </div>
                  )}
                  <div className="mb-5">
                    <InputBox
                      type="email"
                      placeholder="Enter an email address"
                      label="Send us an email message"
                      iconRight={<FiMail />}
                      {...register("details.notifications.0.email")}
                    />
                    <p className='text-error'>{errors.details?.notifications?.[0]?.email?.message}</p>
                  </div>
                  {(isObserver || needContacts) && (
                    <div>
                      <InputBox
                        type="text"
                        label="Call us with an automated voice message"
                        iconRight={<FiPhone />}
                        placeholder="Enter a phone number"
                        {...register("details.notifications.0.voice")}
                      />
                      <p className='text-error'>{errors.details?.notifications?.[0]?.voice?.message}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {needContacts && (
              <div className="flex flex-wrap -mx-4 mt-10">
                <div className="md:w-1/3 px-4">
                  <h2 className='text-base lg:text-lg 2xl:text-2xl font-semibold'>Contact Preferences</h2>
                  <p className='text-gray-600 text-sm 2xl:text-base'>Other agencies may contact me at the following phone number and email address to discuss a referral:</p>
                </div>
                <div className="w-full md:w-2/3 px-4 mt-3 md:mt-0">
                  <div className="card p-6">
                    <div className="mb-5">
                      <InputBox
                        type="text"
                        label="Phone number"
                        iconRight={<FiPhone />}
                        placeholder="Enter a phone number"
                        {...register("details.referral.phone")}
                      />
                      <p className='text-error'>{errors.details?.referral?.phone?.message}</p>
                    </div>
                    <div>
                      <InputBox
                        type="email"
                        label="Email"
                        iconRight={<FiMail />}
                        placeholder="Enter an email address"
                        {...register("details.referral.email")}
                      />
                      <p className='text-error'>{errors.details?.referral?.email?.message}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end items-center mt-4 gap-3">
              <button type="button" className="btn py-2 2xl:py-3 btn-outline-gray transition-all delay-100" onClick={() => {
                if (location.key !== "default") {
                  navigate(-1);
                } else {
                  navigate("/bedfinder", { replace: true });
                }
              }}>
                Go Back
              </button>
              <button type="submit" className="btn-sm btn-primary rounded-lg px-6">Save Settings</button></div>
              {errors.form && <p className='flex justify-end text-error'>{errors.form.message as string}</p>}
          </div>
        </form>
        <ModalDailog handleSubmit={handleSubmit} closeRequest={submit} isOpen={isSettingConfirmModelOpen} onClose={closeIsSettingConfirmModel} title="Confirmation" backButton="No" primaryButton="Yes">
          <p className='text-lg'>You are changing your organization's preferences. Are you sure?</p>
        </ModalDailog>
      </div>
    </>
  )
}

export default SettingsPage